import { useGenderList } from "Hooks/Options/useGenderList";
import { useIdentificationCardList } from "Hooks/Options/useIdentificationCardList";
import { useFormatLocale } from "Hooks/useFormatLocale";
import { AddressType, type PartyDto } from "Infrastructure/Api/Api";
import { isValid } from "date-fns";
import { useCountryNameResolver } from "Translations/countryNames";
import { Resources, useResource } from "Translations/Resources";

const COMPONENT_RESOURCES =
  Resources.Onboarding.Steps.PersonalInformation.ValueLabels;

type InformationField = {
  label: string;
  value: string | null | undefined;
};

type PersonalInformation = {
  personalInfo: Array<InformationField>;
  addressInfo: Array<InformationField>;
  documentInfo: Array<InformationField>;
};

export const usePersonalInformationValues = ({
  firstName,
  lastName,
  personalNumber,
  birthPlace,
  gender,
  nationality: nationalityCode,
  otherNationalities,
  addresses,
  partyIdentification,
}: PartyDto): PersonalInformation => {
  const format = useFormatLocale();
  const { t } = useResource();
  const genders = useGenderList();
  const { getCountryName, getCountryNames } = useCountryNameResolver();
  const { identificationCards } = useIdentificationCardList();

  const {
    identification,
    identificationNumber,
    identificationValidFrom,
    identificationValidTo,
  } = partyIdentification || {};

  const address = addresses?.find(
    (address) => address.type === AddressType.Permanent,
  );

  const streetWithNumber = `${address?.street ?? ""}${
    address?.streetNumber ? ` ${address?.streetNumber}` : ""
  }${address?.orientationNumber ? `/${address?.orientationNumber}` : ""}`;

  const translatedGender = gender
    ? genders.find(({ code }) => code === gender)?.name
    : "";

  const nationality = getCountryName(nationalityCode);

  const documentType = identification
    ? identificationCards.find(({ code }) => code === identification)?.name
    : "";

  const validFrom =
    identificationValidFrom && new Date(identificationValidFrom);
  const formattedValidFrom =
    validFrom && isValid(validFrom) ? format(validFrom) : "???";

  const validTo = identificationValidTo && new Date(identificationValidTo);
  const formattedValidTo =
    validTo && isValid(validTo) ? format(validTo) : "???";

  const documentValidity = `${formattedValidFrom} - ${formattedValidTo}`;

  return {
    personalInfo: [
      {
        label: t(COMPONENT_RESOURCES.PersonalInfo.FirstName),
        value: firstName,
      },
      { label: t(COMPONENT_RESOURCES.PersonalInfo.LastName), value: lastName },
      {
        label: t(COMPONENT_RESOURCES.PersonalInfo.PersonalNumber),
        value: personalNumber,
      },
      {
        label: t(COMPONENT_RESOURCES.PersonalInfo.BirthPlace),
        value: birthPlace,
      },
      {
        label: t(COMPONENT_RESOURCES.PersonalInfo.Gender),
        value: translatedGender,
      },
      {
        label: t(COMPONENT_RESOURCES.PersonalInfo.Nationality),
        value: nationality,
      },
      // Optional — most people have one citizenship, so no empty row for them.
      ...(otherNationalities.length > 0
        ? [
            {
              label: t(Resources.Common.OtherNationalities),
              value: getCountryNames(otherNationalities),
            },
          ]
        : []),
    ],
    addressInfo: [
      {
        label: t(COMPONENT_RESOURCES.AddressInfo.StreetWithNumber),
        value: streetWithNumber,
      },
      {
        label: t(COMPONENT_RESOURCES.AddressInfo.Municipality),
        value: address?.municipality ?? "",
      },
      {
        label: t(COMPONENT_RESOURCES.AddressInfo.PostalCode),
        value: address?.postalCode ?? "",
      },
    ],
    documentInfo: [
      { label: t(COMPONENT_RESOURCES.DocumentInfo.Type), value: documentType },
      {
        label: t(COMPONENT_RESOURCES.DocumentInfo.Number),
        value: identificationNumber,
      },
      {
        label: t(COMPONENT_RESOURCES.DocumentInfo.Validity),
        value: documentValidity,
      },
      // Hidden for now
      // {
      //   label: "Vydal",
      //   value: document?.issued,
      //P },
    ],
  };
};
